import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import {
    PopoverRoot,
    PopoverTrigger,
    PopoverAnchor,
    PopoverPositioner,
} from '@/components/Popover';

const VIEWPORT_PADDING = 16;

interface ChipPopoverProps {
    chip: ReactNode;
    popoverOpen: boolean;
    onPopoverOpenChange: (open: boolean) => void;
    children: ReactNode;
    side?: 'top' | 'bottom' | 'left' | 'right';
    align?: 'start' | 'center' | 'end';
}

export function ChipPopover({
    chip,
    popoverOpen,
    onPopoverOpenChange,
    children,
    side = 'top',
    align = 'center',
}: ChipPopoverProps) {
    const contentRef = useRef<HTMLDivElement | null>(null);

    const clampIntoViewport = useCallback(() => {
        const content = contentRef.current;
        const wrapper = content?.parentElement;
        if (!content || !wrapper) return;
        const top = wrapper.getBoundingClientRect().top;
        const bottom = top + content.offsetHeight;
        let delta = Math.min(0, window.innerHeight - VIEWPORT_PADDING - bottom);
        if (top + delta < VIEWPORT_PADDING) {
            delta = VIEWPORT_PADDING - top;
        }
        content.style.translate = delta ? `0 ${delta}px` : '';
    }, []);

    useEffect(() => {
        if (!popoverOpen) return;
        const frame = requestAnimationFrame(clampIntoViewport);
        const observer = new ResizeObserver(clampIntoViewport);
        if (contentRef.current) observer.observe(contentRef.current);
        window.addEventListener('resize', clampIntoViewport);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', clampIntoViewport);
        };
    }, [popoverOpen, clampIntoViewport]);

    return (
        <PopoverRoot open={popoverOpen} onOpenChange={onPopoverOpenChange}>
            <PopoverTrigger asChild>
                <div className="relative">
                    {chip}
                    <PopoverAnchor asChild>
                        <span
                            aria-hidden
                            className="pointer-events-none absolute top-0 h-full w-0 left-1/2"
                        />
                    </PopoverAnchor>
                </div>
            </PopoverTrigger>
            <PopoverPositioner
                ref={contentRef}
                side={side}
                sideOffset={8}
                align={align}
                collisionPadding={VIEWPORT_PADDING}
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="max-h-[calc(100dvh-32px)]"
            >
                {children}
            </PopoverPositioner>
        </PopoverRoot>
    );
}
